/**
 * Team Tournament Engine — the parts of FIDE C.04.6 that no pairing engine provides.
 *
 * WHAT PAIRS THE TEAMS. Not this file. The team-versus-team pairing is a Swiss pairing
 * played over match points, and it is done by bbpPairings — the same FIDE-endorsed engine
 * that pairs individuals here — driven with one synthetic "player" per team. See
 * js/services/team-pairing-trf.js for how that file is built and
 * js/services/team-pairing-engine.js for the round trip.
 *
 * WHY THE ENGINE CANNOT SIMPLY BE HANDED TEAMS. bbpPairings rejects a TRF containing a
 * 013 (team) record outright — `throw InvalidLineException("team tournaments are not
 * supported")`, vendor/bbpPairings/src/fileformats/trf.cpp:1093. JaVaFo, also vendored
 * here, implements only the individual Dutch system (C.04.3.1); neither 1.4 nor 2.2
 * documents a team mode. The programs that do run team events — Swiss-Manager, VegaTeam,
 * Chess Arbiter — are closed-source desktop applications, not libraries. So the engine is
 * reused for the bracket-and-float machinery, which is the hard, well-tested part, and
 * only the genuinely team-specific rules live here.
 *
 * WHAT LIVES HERE, and why each piece has to:
 *
 *   * COLOURS (art. 4.3). C.04.6 states plainly that articles 6 and 7 of the Basic Rules
 *     never apply to teams: a captain may move players between boards, so there is no
 *     absolute colour constraint and no Dutch-style strong/mild ladder. C.04.6 has its
 *     own Type A / Type B preferences, and — crucially — assigns colours as a SEPARATE
 *     STEP AFTER the pairing is complete (art. 3.2 step 4). Overriding what bbpPairings
 *     returns is therefore not a workaround; it is the order the rulebook itself uses.
 *   * BOARDS. Expanding one team match into individual games is not a pairing problem.
 *   * LINE-UPS. Board-order legality is a competition rule, not a Swiss rule.
 *   * STANDINGS. Team tie-breaks (match points, game points, team SB) are not the
 *     individual ones in js/core/tiebreak.js.
 *
 * Sources: FIDE Handbook C.04.6 (effective 1 February 2026), C.04.1 Basic Rules.
 */

const TEAM_BYE = 'BYE';

const TeamEngine = {

    /**
     * A user-facing message, in the language the app is showing.
     *
     * Goes through the app's translator (window.tr, js/i18n.js) whenever it is loaded,
     * which in the browser is always. Under node:test it is not, so the English template
     * is interpolated directly — which is also why the tests can go on asserting English.
     *
     * Every message is passed as a whole TEMPLATE with {placeholders}, never as a sentence
     * with values already spliced in. The translator looks a message up by its exact text,
     * and "All 7 rounds have already been played" can never match a dictionary key, so a
     * spliced sentence stays in English forever. The pairing services use this too, so there
     * is one implementation of the fallback rather than one per file.
     */
    text(key, params) {
        if (typeof window !== 'undefined' && typeof window.tr === 'function') {
            return window.tr(key, params);
        }
        return String(key).replace(/\{(\w+)\}/g, (m, k) =>
            (params && params[k] !== undefined && params[k] !== null) ? params[k] : m);
    },

    // ---------------------------------------------------------------------------
    // Scoring
    // ---------------------------------------------------------------------------

    /**
     * Game points a single board result is worth to White and to Black.
     *
     * `pointSystem` is the individual one already configured on the tournament
     * ('1-0.5-0' and friends), so a team event scored with football points on the boards
     * behaves the same way it does everywhere else in the app.
     */
    boardPoints(result, pointSystem = '1-0.5-0') {
        const [pWin, pDraw, pLoss] = pointSystem.split('-').map(Number);
        const loss = pLoss || 0;
        switch (result) {
            case '1-0': return { white: pWin, black: loss };
            case '0-1': return { white: loss, black: pWin };
            case '0.5-0.5': return { white: pDraw, black: pDraw };
            // Forfeits: the point is awarded, but no game was played. A zero-point outcome
            // is zero and never pLoss — the same reasoning as js/core/swiss-engine.js,
            // where paying pLoss for a game that never existed made the exported TRF
            // contradict its own points column.
            case '+/-': return { white: pWin, black: 0 };
            case '-/+': return { white: 0, black: pWin };
            case '-/-': return { white: 0, black: 0 };
            default: return { white: 0, black: 0 }; // unplayed / in progress
        }
    },

    /**
     * Match points for one finished match, from its game-point totals.
     *
     * C.04.6 leaves the values to the competition; `matchPointSystem` carries them in the
     * same 'win-draw-loss' shape the individual point system uses. FIDE's default, and the
     * Norwegian one (2 lagpoeng for a win, 1 for a draw), is '2-1-0'.
     */
    matchPoints(whiteGp, blackGp, matchPointSystem = '2-1-0') {
        const [mWin, mDraw, mLoss] = matchPointSystem.split('-').map(Number);
        const loss = mLoss || 0;
        if (whiteGp > blackGp) return { white: mWin, black: loss };
        if (whiteGp < blackGp) return { white: loss, black: mWin };
        return { white: mDraw, black: mDraw };
    },

    /**
     * Walk the rounds and accumulate, per team, everything the pairing file and the
     * standings need.
     *
     * A round carries BOTH the team matches (round.matches) and the flat board games
     * (round.pairings, each tagged with its matchId). Game points are summed from the
     * board games; match points from those sums.
     *
     * @param {Array} teams   [{ id, teamNo, name }]
     * @param {Array} rounds  [{ roundNo, matches: [...], pairings: [...] }]
     * @param {Object} opts   { pointSystem, matchPointSystem, boards }
     * @returns {Object} teamId -> stats
     */
    calculateTeamStats(teams, rounds, opts = {}) {
        const pointSystem = opts.pointSystem || '1-0.5-0';
        const matchPointSystem = opts.matchPointSystem || '2-1-0';

        const stats = {};
        teams.forEach(t => {
            stats[t.id] = {
                id: t.id,
                teamNo: parseInt(t.teamNo, 10) || 0,
                matchPoints: 0,
                gamePoints: 0,
                // Colour of BOARD 1 in each played match, in round order. C.04.6 treats
                // colour as a property of the team, and board 1's colour names it. A PAB
                // round contributes nothing — a bye has no colour (art. 2.5).
                colours: [],
                colourDiff: 0,          // CD: matches with White minus matches with Black
                opponents: new Set(),   // every team met, forfeits included
                matchesPlayed: 0,
                hadBye: false,
                results: []             // [{ roundNo, opponentId, colour, gp, oppGp, mp, decided }]
            };
        });

        rounds.forEach(round => {
            const boardsByMatch = new Map();
            (round.pairings || []).forEach(p => {
                if (!p.matchId) return;
                if (!boardsByMatch.has(p.matchId)) boardsByMatch.set(p.matchId, []);
                boardsByMatch.get(p.matchId).push(p);
            });

            (round.matches || []).forEach(m => {
                const ws = stats[m.whiteTeam];
                const bs = m.blackTeam === TEAM_BYE ? null : stats[m.blackTeam];

                // A pairing-allocated bye (art. 2.5): no opponent, no colour, and
                // draw-equivalent points in BOTH scorings. The game-point half is the draw
                // value across the boards, so a bye neither rewards nor punishes a team in
                // a game-point-primary event.
                if (!bs) {
                    if (!ws) return;
                    const mDraw = Number(matchPointSystem.split('-')[1]);
                    const pDraw = Number(pointSystem.split('-')[1]);
                    const boards = parseInt(m.boards || opts.boards, 10) || 0;
                    ws.matchPoints += (m.byeMatchPoints !== undefined ? Number(m.byeMatchPoints) : mDraw);
                    ws.gamePoints += (m.byeGamePoints !== undefined ? Number(m.byeGamePoints) : pDraw * boards);
                    ws.hadBye = true;
                    ws.results.push({ roundNo: round.roundNo, opponentId: TEAM_BYE, colour: null, gp: 0, oppGp: 0, mp: mDraw, decided: true });
                    return;
                }
                if (!ws) return;

                const boardGames = boardsByMatch.get(m.id) || [];
                let whiteGp = 0, blackGp = 0;
                boardGames.forEach(p => {
                    // p.white / p.black are PLAYER ids. Which side of the board belongs to
                    // which TEAM alternates down the boards, so the pairing records it
                    // rather than the reader re-deriving it from the board number.
                    const pts = TeamEngine.boardPoints(p.result, pointSystem);
                    if (p.whiteTeam === m.whiteTeam) {
                        whiteGp += pts.white; blackGp += pts.black;
                    } else {
                        whiteGp += pts.black; blackGp += pts.white;
                    }
                });

                ws.gamePoints += whiteGp;
                bs.gamePoints += blackGp;

                // Match points only once every board is in. A half-finished match shows its
                // game points but awards no match points, which is what an arbiter watching
                // a running round expects to see.
                const decided = boardGames.length > 0 && boardGames.every(p => p.result);
                const mpOf = decided
                    ? TeamEngine.matchPoints(whiteGp, blackGp, matchPointSystem)
                    : { white: 0, black: 0 };
                if (decided) {
                    ws.matchPoints += mpOf.white;
                    bs.matchPoints += mpOf.black;
                }

                // The pairing itself always counts — it is what [C1] forbids repeating and
                // what direct encounter needs to know happened. `matchesPlayed`, by
                // contrast, counts only FINISHED matches: it is the "Pld" column, and a
                // round that has been paired but not played has not been played.
                ws.opponents.add(bs.id);
                bs.opponents.add(ws.id);
                if (decided) { ws.matchesPlayed++; bs.matchesPlayed++; }
                ws.colours.push('W'); bs.colours.push('B');
                ws.colourDiff++; bs.colourDiff--;

                ws.results.push({ roundNo: round.roundNo, opponentId: bs.id, colour: 'W', gp: whiteGp, oppGp: blackGp, mp: mpOf.white, decided });
                bs.results.push({ roundNo: round.roundNo, opponentId: ws.id, colour: 'B', gp: blackGp, oppGp: whiteGp, mp: mpOf.black, decided });
            });
        });

        return stats;
    },

    // ---------------------------------------------------------------------------
    // Colour preferences — C.04.6 art. 2.9 / 2.10
    // ---------------------------------------------------------------------------

    /**
     * The colour this team prefers, and how badly.
     *
     * Type A (the default) knows only "a preference" or "none". Type B splits that into
     * strong and mild, and exists because some competitions want colour weighted more
     * heavily than the default does. Neither is ever ABSOLUTE — that is the whole point of
     * excluding Basic Rules art. 6 and 7 from team play.
     *
     * @returns {{ colour: 'W'|'B'|null, strength: 'strong'|'mild'|'none' }}
     */
    colourPreference(stat, type = 'A', isFinalRound = false) {
        const cd = stat.colourDiff;
        const n = stat.colours.length;
        const last = n > 0 ? stat.colours[n - 1] : null;
        const prev = n > 1 ? stat.colours[n - 2] : null;
        const lastTwoBlack = last === 'B' && prev === 'B';
        const lastTwoWhite = last === 'W' && prev === 'W';

        // A team that has not played has nothing to prefer from; art. 4.3.1 seeds its
        // colour from the TPN instead.
        if (n === 0) return { colour: null, strength: 'none' };

        const strongWhite = cd < -1 || ((cd === 0 || cd === -1) && lastTwoBlack);
        const strongBlack = cd > 1 || ((cd === 0 || cd === 1) && lastTwoWhite);

        if (strongWhite) return { colour: 'W', strength: 'strong' };
        if (strongBlack) return { colour: 'B', strength: 'strong' };

        if (type === 'B') {
            // Mild preferences exist only under Type B. In the FINAL round a CD of zero
            // yields no preference at all — there is no later round left to balance.
            if (cd === -1 || (cd === 0 && !isFinalRound && last === 'B')) return { colour: 'W', strength: 'mild' };
            if (cd === 1 || (cd === 0 && !isFinalRound && last === 'W')) return { colour: 'B', strength: 'mild' };
        }

        return { colour: null, strength: 'none' };
    },

    /**
     * The order C.04.6 art. 4.4 calls "first-team": higher primary score, then higher
     * secondary score, then LOWER TPN. Used both to rank a scoregroup and to decide which
     * of two teams speaks first when colours are settled.
     */
    orderTeams(teamIds, stats, primaryScore = 'match') {
        const primary = (s) => primaryScore === 'game' ? s.gamePoints : s.matchPoints;
        const secondary = (s) => primaryScore === 'game' ? s.matchPoints : s.gamePoints;
        return [...teamIds].sort((a, b) => {
            const sa = stats[a], sb = stats[b];
            return (primary(sb) - primary(sa))
                || (secondary(sb) - secondary(sa))
                || (sa.teamNo - sb.teamNo);
        });
    },

    /**
     * Whether each team floated UP or DOWN in each round it played.
     *
     * C.04.6 [C7] and [C10] want to avoid making the same team float twice running, so the
     * pairer needs last round's float. It is DERIVED here rather than stored: a team
     * floated up in round r if its score going INTO that round was lower than its
     * opponent's. Recomputing it from the rounds means it can never drift out of step with
     * the results, which a stored flag would the first time a result was corrected.
     *
     * Costs one pass over the rounds per round, which is nothing for the ten or fifteen a
     * team event plays.
     *
     * @returns {Object} teamId -> array of 'up' | 'down' | null, one per round played
     */
    floatHistory(teams, rounds, opts = {}) {
        const primaryScore = opts.primaryScore || 'match';
        const primaryOf = (st) => primaryScore === 'game' ? st.gamePoints : st.matchPoints;

        const out = {};
        teams.forEach(t => { out[t.id] = []; });

        rounds.forEach((round, i) => {
            // Scores as they stood BEFORE this round — that is what the float was measured
            // against when the round was paired.
            const before = TeamEngine.calculateTeamStats(teams, rounds.slice(0, i), opts);
            (round.matches || []).forEach(m => {
                if (m.blackTeam === TEAM_BYE) {
                    if (out[m.whiteTeam]) out[m.whiteTeam].push(null);
                    return;
                }
                const w = before[m.whiteTeam], b = before[m.blackTeam];
                if (!w || !b) return;
                const dw = primaryOf(w), db = primaryOf(b);
                const wFloat = dw === db ? null : (dw < db ? 'up' : 'down');
                const bFloat = dw === db ? null : (dw < db ? 'down' : 'up');
                if (out[m.whiteTeam]) out[m.whiteTeam].push(wFloat);
                if (out[m.blackTeam]) out[m.blackTeam].push(bFloat);
            });
        });

        return out;
    },

    /**
     * Which team plays White on board 1 — C.04.6 art. 4.3, in its stated priority order.
     *
     * "White" for a team means White on the odd boards; expandMatch() below turns that
     * into the actual board colours.
     *
     * This is applied to the pairing bbpPairings returned, discarding the colours it chose
     * under the individual Dutch rules. That is the rulebook's own sequencing: art. 3.2
     * pairs first and allocates colours as a separate final step.
     */
    assignMatchColours(aId, bId, stats, opts = {}) {
        const type = opts.colourPrefType || 'A';
        const isFinal = !!opts.isFinalRound;
        const initialColour = opts.initialColour === 'black' ? 'B' : 'W';
        const primaryScore = opts.primaryScore || 'match';

        const ordered = TeamEngine.orderTeams([aId, bId], stats, primaryScore);
        const firstId = ordered[0];
        const secondId = ordered[1];
        const first = stats[firstId], second = stats[secondId];

        const give = (whiteId) => ({ white: whiteId, black: whiteId === aId ? bId : aId });

        // 4.3.1 — neither team has played: the first-team's TPN parity decides. An odd TPN
        // gets the tournament's drawn initial colour, an even one the opposite.
        if (first.colours.length === 0 && second.colours.length === 0) {
            const firstGetsInitial = (first.teamNo % 2) === 1;
            const firstColour = firstGetsInitial ? initialColour : (initialColour === 'W' ? 'B' : 'W');
            return give(firstColour === 'W' ? firstId : secondId);
        }

        const pa = TeamEngine.colourPreference(first, type, isFinal);
        const pb = TeamEngine.colourPreference(second, type, isFinal);

        // 4.3.2 — exactly one team has a preference: grant it.
        if (pa.colour && !pb.colour) return give(pa.colour === 'W' ? firstId : secondId);
        if (!pa.colour && pb.colour) return give(pb.colour === 'W' ? secondId : firstId);

        // 4.3.3 — opposite preferences: both are satisfiable, so satisfy both.
        if (pa.colour && pb.colour && pa.colour !== pb.colour) {
            return give(pa.colour === 'W' ? firstId : secondId);
        }

        // 4.3.4 — Type B only: same preference, but exactly one of them is strong.
        if (type === 'B' && pa.colour && pb.colour && pa.colour === pb.colour) {
            if (pa.strength === 'strong' && pb.strength !== 'strong') return give(pa.colour === 'W' ? firstId : secondId);
            if (pb.strength === 'strong' && pa.strength !== 'strong') return give(pb.colour === 'W' ? secondId : firstId);
        }

        // 4.3.5 — the lower colour difference takes White (it is the team more owed it).
        if (first.colourDiff !== second.colourDiff) {
            return give(first.colourDiff < second.colourDiff ? firstId : secondId);
        }

        // 4.3.6 — alternate from the most recent round in which the two held different
        // colours, walking backwards.
        const len = Math.min(first.colours.length, second.colours.length);
        for (let i = len - 1; i >= 0; i--) {
            if (first.colours[i] !== second.colours[i]) {
                return give(first.colours[i] === 'B' ? firstId : secondId);
            }
        }

        // 4.3.7 — grant the first-team's own preference, whatever its strength.
        if (pa.colour) return give(pa.colour === 'W' ? firstId : secondId);

        // 4.3.8 / 4.3.9 — nothing left to go on: alternate each team's last colour.
        const lastFirst = first.colours[first.colours.length - 1];
        if (lastFirst) return give(lastFirst === 'B' ? firstId : secondId);
        const lastSecond = second.colours[second.colours.length - 1];
        if (lastSecond) return give(lastSecond === 'B' ? secondId : firstId);

        return give(firstId);
    },

    /**
     * Colour a whole round of team pairings.
     *
     * @param {Array} pairs [[teamIdA, teamIdB], ...] — order within a pair is irrelevant
     * @returns {Array} [{ whiteTeam, blackTeam }]
     */
    assignRoundColours(pairs, stats, opts = {}) {
        return pairs.map(([a, b]) => TeamEngine.assignMatchColours(a, b, stats, opts))
            .map(({ white, black }) => ({ whiteTeam: white, blackTeam: black }));
    },

    /**
     * The one place where driving bbpPairings with team data can legitimately diverge from
     * C.04.6, reported rather than hidden.
     *
     * The engine applies Basic Rules art. 6 and 7 — colour difference confined to ±2, never
     * three of the same colour running — as ABSOLUTE constraints, and C.04.6 excludes both
     * from team play. Those constraints can only change who plays whom once a team is at
     * the edge of them, and art. 4.3.5 ("lower CD takes White") normally keeps every CD
     * inside {-1, 0, +1}, so the situation is rare. When it does arise the arbiter is told,
     * instead of the round quietly being paired under the wrong rulebook.
     *
     * @returns {Array<{teamId, reason}>} empty when nothing is near the Dutch limits
     */
    colourRuleRisks(teams, stats) {
        const risks = [];
        teams.forEach(t => {
            const s = stats[t.id];
            if (!s) return;
            const n = s.colours.length;
            if (Math.abs(s.colourDiff) >= 2) {
                const cd = `${s.colourDiff > 0 ? '+' : ''}${s.colourDiff}`;
                risks.push({ teamId: t.id, reason: TeamEngine.text('colour difference is {cd}', { cd }) });
            } else if (n >= 2 && s.colours[n - 1] === s.colours[n - 2]) {
                // Two keys rather than one with the colour spliced in: the colour's own name
                // has to be translated too, and inside the sentence it is not a key.
                risks.push({
                    teamId: t.id,
                    reason: TeamEngine.text(s.colours[n - 1] === 'W'
                        ? 'has had White twice running'
                        : 'has had Black twice running')
                });
            }
        });
        return risks;
    },

    // ---------------------------------------------------------------------------
    // Boards
    // ---------------------------------------------------------------------------

    /**
     * Turn one team match into individual board games.
     *
     * The team holding White plays White on boards 1, 3, 5 … and Black on 2, 4, 6 …; its
     * opponent gets the mirror. Each emitted pairing carries `matchId`, `board` and
     * `whiteTeam`/`blackTeam`, so mapping a board result back to a team score never has to
     * be guessed from the board number.
     *
     * The shape is otherwise exactly the {white, black, result} the rest of the app already
     * handles, which is why result entry, the individual standings, TRF export and the
     * rating engine all keep working on a team tournament without knowing it is one.
     *
     * A board with nobody fielded on one side is a forfeit for that board, not a missing
     * game — a short-handed team loses the point and the match still resolves.
     *
     * @param {Object} match       { id, whiteTeam, blackTeam }
     * @param {Array} whiteLineup  player ids, board 1 first ('' / null for an empty board)
     * @param {Array} blackLineup  player ids, board 1 first
     * @param {number} boards
     */
    expandMatch(match, whiteLineup, blackLineup, boards) {
        const out = [];
        for (let b = 1; b <= boards; b++) {
            const wSidePlayer = (whiteLineup || [])[b - 1] || null;
            const bSidePlayer = (blackLineup || [])[b - 1] || null;

            // Odd boards: the White team is White. Even boards: it is Black.
            const whiteTeamIsWhiteHere = (b % 2 === 1);
            const whitePlayer = whiteTeamIsWhiteHere ? wSidePlayer : bSidePlayer;
            const blackPlayer = whiteTeamIsWhiteHere ? bSidePlayer : wSidePlayer;
            const whiteSideTeam = whiteTeamIsWhiteHere ? match.whiteTeam : match.blackTeam;
            const blackSideTeam = whiteTeamIsWhiteHere ? match.blackTeam : match.whiteTeam;

            let result = null;
            if (!whitePlayer && !blackPlayer) result = '-/-';
            else if (!whitePlayer) result = '-/+';
            else if (!blackPlayer) result = '+/-';

            out.push({
                matchId: match.id,
                board: b,
                white: whitePlayer || 'WO',
                black: blackPlayer || 'WO',
                whiteTeam: whiteSideTeam,
                blackTeam: blackSideTeam,
                result
            });
        }
        return out;
    },

    // ---------------------------------------------------------------------------
    // Line-ups
    // ---------------------------------------------------------------------------

    /**
     * Is this line-up legal?
     *
     * The rule every team competition shares: a squad is registered in a FIXED board order
     * (strongest first), and the players fielded in a round must appear in that same
     * RELATIVE order. A reserve may come in, but only below the players registered ahead of
     * them — that is what stops a captain sandbagging board 1 to steal a match.
     *
     * @param {Array} squad   the team's registered order (index 0 = board 1)
     * @param {Array} lineup  ids fielded this round, board 1 first
     * @returns {{ ok: boolean, errors: string[] }}
     */
    validateLineup(squad, lineup, boards, opts = {}) {
        const errors = [];
        const filled = (lineup || []).filter(id => id);

        // Whole sentences, with nothing interpolated into them: these are shown through
        // the app's translator (tr) in js/ui/teams.js, which looks the message up by its
        // exact English text. A message with a number spliced into it can never match a
        // dictionary key, so it would be stuck in English. The counts are on screen
        // anyway — the team card carries an "n of b" badge.
        if (filled.length > boards) {
            errors.push('More players are fielded than the match has boards.');
        }

        if (filled.some((id, i) => filled.indexOf(id) !== i)) {
            errors.push('The same player is fielded on more than one board.');
        }

        if (filled.some(id => !squad.includes(id))) {
            errors.push('A player who is not registered for this team was fielded.');
        }

        // The relative-order test: squad positions must ascend down the boards.
        const positions = filled.map(id => squad.indexOf(id)).filter(i => i >= 0);
        for (let i = 1; i < positions.length; i++) {
            if (positions[i] < positions[i - 1]) {
                errors.push('The board order is broken — a player registered lower in the squad is fielded above one registered higher.');
                break;
            }
        }

        // Some competitions require the boards to be filled from the top, i.e. reserves may
        // play the bottom boards only. Off by default, because plenty of events let a
        // short-handed team leave any board empty.
        if (opts.reservesBottomOnly) {
            const firstEmpty = (lineup || []).slice(0, boards).findIndex(id => !id);
            if (firstEmpty !== -1 && (lineup || []).slice(firstEmpty, boards).some(id => id)) {
                errors.push('Boards must be filled from the top — reserves may only play the bottom boards.');
            }
        }

        return { ok: errors.length === 0, errors };
    },

    /**
     * The line-up a team gets when its captain has not chosen one: the top `boards`
     * available players of the registered squad.
     */
    defaultLineup(squad, boards, isAvailable = () => true) {
        return squad.filter(isAvailable).slice(0, boards);
    },

    // ---------------------------------------------------------------------------
    // Standings
    // ---------------------------------------------------------------------------

    /**
     * Team standings, ordered by the configured tie-break list.
     *
     * Supported keys:
     *   'mp'   match points
     *   'gp'   game (board) points
     *   'de'   direct encounter, applied only when every tied team has met every other
     *   'ttsb' team Sonneborn-Berger over the primary score
     *   'tbh'  team Buchholz — the sum of the opponents' primary scores
     *   'mpo'  match points of the opponents faced
     *
     * The primary score always leads, so a competition ranked by board points
     * (brettpoeng) rather than match points is one setting away, not a second code path.
     */
    calculateTeamStandings(teams, rounds, opts = {}) {
        const primaryScore = opts.primaryScore || 'match';
        const tiebreaks = opts.tiebreaks || ['gp', 'de', 'ttsb'];
        const stats = TeamEngine.calculateTeamStats(teams, rounds, opts);

        const primaryOf = (s) => primaryScore === 'game' ? s.gamePoints : s.matchPoints;

        const rows = teams.map(t => {
            const s = stats[t.id];
            // Only FINISHED matches feed a tie-break. A round that has been paired but not
            // yet played would otherwise enter every sum as a 0-0 draw, which quietly
            // reorders the table the moment the next round is generated — teams level on
            // everything would separate purely because they had been given opponents.
            const opps = s.results.filter(r => r.opponentId !== TEAM_BYE && r.decided);
            const tb = {};

            tb.mp = s.matchPoints;
            tb.gp = s.gamePoints;
            tb.tbh = opps.reduce((sum, r) => sum + primaryOf(stats[r.opponentId]), 0);
            tb.mpo = opps.reduce((sum, r) => sum + stats[r.opponentId].matchPoints, 0);
            // Team Sonneborn-Berger: each opponent's primary score weighted by how the
            // match against them went — full for a win, half for a draw.
            tb.ttsb = opps.reduce((sum, r) => {
                const oppPrimary = primaryOf(stats[r.opponentId]);
                if (r.gp > r.oppGp) return sum + oppPrimary;
                if (r.gp === r.oppGp) return sum + oppPrimary / 2;
                return sum;
            }, 0);

            return {
                teamId: t.id,
                teamNo: s.teamNo,
                name: t.name,
                matchPoints: s.matchPoints,
                gamePoints: s.gamePoints,
                matchesPlayed: s.matchesPlayed,
                primary: primaryOf(s),
                tb,
                stats: s
            };
        });

        // Direct encounter decides a tie only when every tied team has played every other
        // one; otherwise it would compare scores drawn from unrelated games. Resolved per
        // tie group, so it is always evaluated against the right set of teams.
        const directEncounter = (group) => {
            const ids = new Set(group.map(r => r.teamId));
            const complete = group.every(r =>
                group.every(o => o.teamId === r.teamId || r.stats.opponents.has(o.teamId)));
            if (!complete) return null;
            const scores = new Map();
            group.forEach(r => {
                scores.set(r.teamId, r.stats.results
                    .filter(x => ids.has(x.opponentId))
                    .reduce((sum, x) => sum + x.mp, 0));
            });
            return scores;
        };

        rows.sort((a, b) => (b.primary - a.primary) || (a.teamNo - b.teamNo));

        const out = [];
        let i = 0;
        while (i < rows.length) {
            let j = i;
            while (j < rows.length && rows[j].primary === rows[i].primary) j++;
            const group = rows.slice(i, j);

            if (group.length > 1) {
                const de = tiebreaks.includes('de') ? directEncounter(group) : null;
                group.sort((a, b) => {
                    for (const key of tiebreaks) {
                        if (key === 'de') {
                            if (!de) continue;
                            const d = de.get(b.teamId) - de.get(a.teamId);
                            if (d !== 0) return d;
                            continue;
                        }
                        const d = (b.tb[key] || 0) - (a.tb[key] || 0);
                        if (d !== 0) return d;
                    }
                    return a.teamNo - b.teamNo;
                });
            }

            group.forEach(r => out.push(r));
            i = j;
        }

        // Shared ranks: teams that came out equal on every configured criterion.
        let rank = 0;
        out.forEach((row, idx) => {
            const prev = out[idx - 1];
            const same = prev
                && prev.primary === row.primary
                && tiebreaks.every(k => k === 'de' || (prev.tb[k] || 0) === (row.tb[k] || 0));
            if (!same) rank = idx + 1;
            row.rank = rank;
        });

        return out;
    }
};


if (typeof module !== 'undefined') {
    module.exports = { TeamEngine, TEAM_BYE };
} else {
    window.TeamEngine = TeamEngine;
}
